import type { Tables } from '../../types/database.types';

export type SavedViewRow = Tables<'saved_views'>;

export type SavedViewMode = 'list' | 'board';

/**
 * The filter set a view can hold (handoff 25). Every field optional: absent
 * means "not filtering on this". Stored as JSON in saved_views.filters, so
 * this type is the single owner of that shape — the database checks only
 * that it is an object.
 */
export interface SavedViewFilters {
  assigneeId?: string | null;
  statusId?: string | null;
  sectionId?: string | null;
  priority?: 'high' | 'medium' | 'low';
  dueWithin?: 'overdue' | 'today' | 'tomorrow' | 'this_week' | 'none';
  isCompleted?: boolean;
  hasComments?: boolean;
  hasSubtasks?: boolean;
  /** Free-text search over titles, descriptions and remarks (handoff 25). */
  search?: string;
}

export interface SavedViewSort {
  column?: 'position' | 'title' | 'assignee' | 'status' | 'due_date' | 'priority' | 'created_at';
  direction?: 'asc' | 'desc';
}

/** A saved view with its JSON parts parsed into their owned shapes. */
export interface SavedView {
  id: string;
  project_id: string | null;
  name: string;
  view_mode: SavedViewMode;
  filters: SavedViewFilters;
  sort: SavedViewSort;
  group_by: string | null;
  visible_columns: string[] | null;
  position: number;
}

export interface SavedViewInput {
  projectId: string | null;
  name: string;
  viewMode: SavedViewMode;
  filters: SavedViewFilters;
  sort: SavedViewSort;
  groupBy: string | null;
  visibleColumns: string[] | null;
}

export const SAVED_VIEW_NAME_MAX_LENGTH = 80;

/** Gapped positions, matching tasks and sections. */
export const SAVED_VIEW_POSITION_GAP = 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Reads a stored row into the typed shape. Unknown keys are dropped and a
 * malformed part becomes an empty object: an old or hand-edited row degrades
 * to "no filters" rather than crashing the toolbar.
 */
export function parseSavedView(row: SavedViewRow): SavedView {
  const filters = isRecord(row.filters) ? (row.filters as SavedViewFilters) : {};
  const sort = isRecord(row.sort) ? (row.sort as SavedViewSort) : {};
  return {
    id: row.id,
    project_id: row.project_id,
    name: row.name,
    view_mode: row.view_mode === 'board' ? 'board' : 'list',
    filters,
    sort,
    group_by: row.group_by,
    visible_columns: row.visible_columns,
    position: row.position,
  };
}
